import * as React from "react";
import {
  Box,
  Button,
  Stack,
  Typography,
  Alert,
  CircularProgress,
  Grid,
  Collapse,
  InputAdornment,
  Chip,
  IconButton,
  Divider,
  alpha,
  useTheme,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { toast } from "react-toastify";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import RefreshIcon from "@mui/icons-material/Refresh";
import SaveIcon from "@mui/icons-material/Save";
import { useTitle } from "../../hooks/useTitle";
import { CardBase, StyledTextField } from "../../components/global";
import {
  useGetUILabelsQuery,
  useUpdateUILabelsMutation,
  useCreateUILabelsMutation,
} from "../../services/uiLabel.service";
import { getAllLabelKeys, getDefaultLabel, UILabelKey, COMMON_LABELS, PROJECT_LABELS, TALENT_LABELS, CLIENT_LABELS, MANAGER_LABELS, LOCATION_LABELS } from "../../ui-labels";
import useDebounce from "../../hooks/useDebounce";

// comment
// Pre-compute label parts to avoid repeated splits
const getLabelParts = (labelKey: string) => {
  const parts = labelKey.split(".");
  return { module: parts[0], labelName: parts[1] };
};

// Categorize labels for better organization
const categorizeLabel = (module: string, labelName: string): string => {
  if (module === "common") {
    // Sidebar Menu - dedicated section for all sidebar menu items
    if (["dashboard", "workMenu", "createNewWork", "workCalendar", "workManagement", "locations", "templates", "independentProfessionals", "showList", "showMap", "favorites", "finances", "generalFinances", "financialReport", "settings", "addManagers", "uiWording"].includes(labelName)) {
      return "Sidebar Menu";
    }
    if (["create", "update", "save", "cancel", "delete", "edit", "add", "submit", "close", "back", "next", "previous", "refresh", "restore"].includes(labelName)) {
      return "Actions & Buttons";
    }
    if (["location", "locationAddress", "locationName", "addLocation", "createLocation", "editLocation", "client", "clients", "clientAddress", "clientName", "addClient", "createClient", "editClient", "address", "name", "description", "phoneNumber", "email", "city", "state", "zip", "fullName", "contactName", "phonenumber"].includes(labelName)) {
      return "Forms & Fields";
    }
    return "General";
  }

  if (module === "project") {
    // Project labels categories
    if (["workInformation", "workSite", "whereIsWorkSite", "selectLocation", "addNewLocation", "location", "locationAddress", "startDateTime", "dateTimeInstructions", "startManagingTime", "selectTimezoneOfWork", "enterSOW", "sowDescription", "scopeOfWork", "workIdWorkTitle", "numberOfIPsRequired", "numberOfIPsRequiredShort", "numberOfIPs"].includes(labelName)) {
      return "Create Job Form";
    }
    if (["pricingDetails", "typeHourlyRate", "enterFixedRate", "hourlyRate", "fixedRate", "fixedRateLabel", "paymentType", "estimatedAmountOfHours", "worksiteRadiusMiles", "onlyAvailableToIPsWithinRadius", "pricing", "rate", "maxHour"].includes(labelName)) {
      return "Pricing & Payment";
    }
    if (["whatSkillsLookingFor", "selectSearchSkills", "skills", "requiredSkills", "requiredCertificationsOptional", "selectCertifications", "certifications", "backgroundCheckRequiredLabel", "drugTestRequiredLabel", "requirements", "notRequired"].includes(labelName)) {
      return "Requirements & Skills";
    }
    if (["assignWorkManagerForWork", "selectWorkManager", "selectAManager", "assignManager", "changeWorkManager"].includes(labelName)) {
      return "Manager Assignment";
    }
    if (["tasks", "tasksOptionalNote", "addTask", "noTasksSpecified", "addTasksIfNeeded"].includes(labelName)) {
      return "Tasks";
    }
    if (["additionalInformation", "finalStepAdditionalInformation", "photosRequiredNote", "finalNotesRequiredNote", "toolsRequiredNote", "confidentialInformationNote", "confidentialInformation", "privateOnlyInvitedIPs", "publicAllIPs", "private", "photosRequired", "finalNotesRequired", "toolsRequired", "additionalSettings"].includes(labelName)) {
      return "Additional Information";
    }
    if (["draft", "openWork", "scheduled", "inProgress", "completed", "cancelled", "draftStatus", "publishedStatus", "scheduledStatus", "inProgressStatus", "completedStatus", "cancelledStatus"].includes(labelName)) {
      return "Status";
    }
    if (["createProject", "editProject", "createNewWork", "bulkUploadWork", "createNewTemplate", "editTemplate", "publishWork", "cancelReason", "cancelWork", "completeWork", "publishWorkQuestion", "completeWorkQuestion", "inviteIndependentProfessional", "refresh"].includes(labelName)) {
      return "Actions";
    }
    if (["project", "projects", "work", "works", "projectDetails", "workManagement", "overview", "details", "openDetail", "moreDetail"].includes(labelName)) {
      return "Project Overview";
    }
    if (["templates", "createNewTemplate", "editTemplate", "templateTitle", "templateInstructions", "selectTemplate", "loadingTemplate", "customizeLayout"].includes(labelName)) {
      return "Templates";
    }
    if (["workId", "workName", "workIdWorkTitle", "title", "lastUpdate", "createdAt", "startTime", "startAt", "reasonSchedule", "viewEditDelete"].includes(labelName)) {
      return "Work Details";
    }
    if (["workDocuments", "noDocumentsSpecified", "viewDocument", "uploadDocument", "uploadDocuments"].includes(labelName)) {
      return "Documents";
    }
    if (["independentProfessional", "independentProfessionals", "assignedIndependentProfessional", "independentProfessionalEmail", "independentProfessionalPhone", "offers", "invites"].includes(labelName)) {
      return "Independent Professionals";
    }
    if (["bulkUpload", "bulkUploadTitle", "uploadMultipleWorks", "bulkUploadWork"].includes(labelName)) {
      return "Bulk Upload";
    }
    if (["chats", "internalNotes", "metadata", "workMap", "noWorkYet", "showMap"].includes(labelName)) {
      return "Work Management";
    }
    if (["client", "clientAddress", "clientName", "selectClient", "addNewClient", "locationType", "siteNumber"].includes(labelName)) {
      return "Client & Location";
    }
    if (["workPublished", "workCompleted", "privatePosting", "publicPosting", "projectCanceled", "independentProfessionalCanceled", "workRejected"].includes(labelName)) {
      return "Status Messages";
    }
    if (["yes", "no", "mileRadius", "goTo", "addCertificationsIfNeeded", "user", "time", "eventName", "nA"].includes(labelName)) {
      return "General";
    }
    if (["unableToFindTalent", "joinedChatRoom", "fileSizeTooSmall", "fileSizeTooLarge", "problemReadingFile", "tooManyFiles", "notEnoughFiles"].includes(labelName)) {
      return "Error & System Messages";
    }
    if (["sendOtherExpenses", "bonus", "additionalHours", "reimbursement", "selectType", "amount", "reason", "otherExpensesSentSuccessfully", "pleaseEnterValidAmount", "pleaseProvideReason", "recordOtherExpensesDescription", "cancelProject", "selectCancelReason", "removeIndependentProfessional", "jobInternalNotes", "notes", "notVisibleToIP", "professionalsWorkReview", "selectReviewReason", "editStartTime", "startWorkingTime", "selectRescheduleReason", "createTask", "editTask", "taskName", "taskDescription", "uploadDocumentsDescription", "chooseFile", "attachingFileToProject", "uploadSuccessful", "pleaseChooseFile", "loadingWork"].includes(labelName)) {
      return "Modal Forms";
    }
    if (["matchScore", "totalScore", "skillMatch", "distance", "available", "currentlyHired", "noAvailabilitySet", "position", "yearsOfExperience", "matchedWithProject", "otherSkills", "notMatched"].includes(labelName)) {
      return "Talent Matching";
    }
    if (["joinChat", "typeMessageHere"].includes(labelName)) {
      return "Work Management";
    }
    if (["createTemplateFromThisWork"].includes(labelName)) {
      return "Templates";
    }
    return "Other";
  }

  if (module === "talent") {
    // Talent labels categories
    if (["talent", "talents", "independentProfessionals", "favoriteTalents", "talentDetails", "independentProfessionalsMap", "favoriteIndependentProfessionals"].includes(labelName)) {
      return "Talent Overview";
    }
    if (["skills", "certifications", "experience", "rating", "reviews", "industries", "languages", "availability", "workStatus", "noIndustryAdded", "noSkillAdded", "noLanguagesAdded", "notSet"].includes(labelName)) {
      return "Talent Information";
    }
    if (["showMap", "favorites", "favorited", "addAsFavorite", "searchingIndependentProfessionals", "loadingMaps"].includes(labelName)) {
      return "Talent Actions";
    }
    if (["soleProprietor", "entrepreneur", "partTime", "partTimeProfessional"].includes(labelName)) {
      return "Work Status Values";
    }
    if (["unknownCity", "unknownState", "noEmail", "noPhone", "memberSince", "location", "locationAddress", "phonenumber"].includes(labelName)) {
      return "Location & Contact";
    }
    if (["workSuccess", "totalEarned", "work"].includes(labelName)) {
      return "Talent Statistics";
    }
    if (["successfullyAddAsFavorite", "successfullyRemoveFromFavorite", "successfullyDeleted", "deleteFromFavorite"].includes(labelName)) {
      return "Success Messages";
    }
    if (["driverLicense", "workPermit", "insurance", "specialDocuments", "untitledDocument", "viewDocument"].includes(labelName)) {
      return "Document Types";
    }
    if (["moreInfo", "rate", "commuteRadius", "allowBackgroundCheck", "allowDrugTest", "moreDetail"].includes(labelName)) {
      return "Talent Drawer";
    }
    if (["about", "noBioAvailable", "workExperience", "education", "degree", "fieldOfStudy", "present", "licenseCertificates", "number", "expires", "contact", "address1", "address2", "cityZip", "country", "monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday", "backgroundCheck", "drugTest", "validUSCitizenOrHaveWorkVisa"].includes(labelName)) {
      return "Talent Profile";
    }
    if (["typeOfWork", "reviewsFor", "noReviewYet", "untitledProject", "unknownCompany"].includes(labelName)) {
      return "Talent Pages";
    }
    if (["facebook", "instagram", "linkedIn"].includes(labelName)) {
      return "Social Media";
    }
    if (["nA"].includes(labelName)) {
      return "General";
    }
    return "Other";
  }

  if (module === "client") {
    // Client labels categories
    if (["client", "clients", "clientName", "clientAddress", "clientDetails"].includes(labelName)) {
      return "Client Overview";
    }
    if (["createClient", "editClient", "addClient", "selectClient", "addNewClient", "clientCreated", "clientUpdated"].includes(labelName)) {
      return "Client Actions";
    }
    if (["active", "completed", "cancelled", "all", "profile", "about", "contact", "socialMedias", "address1", "address2", "cityZip", "unknownCity", "unknownState"].includes(labelName)) {
      return "Client Information";
    }
    if (["general", "searchNewAddress", "deleteClientQuestion"].includes(labelName)) {
      return "Client Form";
    }
    if (["linkedIn", "facebook", "instagram", "website", "linkedInURL", "facebookURL", "instagramURL", "websiteURL", "linkedInColon", "facebookColon", "instagramColon", "websiteColon"].includes(labelName)) {
      return "Social Media";
    }
    if (["nA"].includes(labelName)) {
      return "General";
    }
    return "Other";
  }

  if (module === "manager") {
    // Manager labels categories
    if (["manager", "managers", "managerDetails", "workManagers"].includes(labelName)) {
      return "Manager Overview";
    }
    if (["addManager", "addManagers", "createManager", "editManager"].includes(labelName)) {
      return "Manager Actions";
    }
    if (["firstName", "lastName", "phoneNumber", "email", "password"].includes(labelName)) {
      return "Manager Form Fields";
    }
    if (["firstNameRequired", "lastNameRequired", "emailRequired", "invalidEmailAddress", "passwordRequired", "leaveBlankIfDontWantToChangePassword", "passwordRequirement"].includes(labelName)) {
      return "Manager Validation";
    }
    if (["deleteManagerQuestion", "dataRestored", "nA"].includes(labelName)) {
      return "Manager Messages";
    }
    return "Other";
  }

  if (module === "location") {
    // Location labels categories
    if (["location", "locations", "locationName", "locationAddress", "locationDetails", "locationsManagement"].includes(labelName)) {
      return "Location Overview";
    }
    if (["addLocation", "createLocation", "editLocation", "selectLocation", "addNewLocation"].includes(labelName)) {
      return "Location Actions";
    }
    if (["country", "contactName", "contactPhone", "contactEmail", "primaryContact", "fullName", "nA"].includes(labelName)) {
      return "Location Details";
    }
    if (["address1", "address2", "searchNewAddress", "siteNumber"].includes(labelName)) {
      return "Location Form";
    }
    if (["deleteLocationQuestion"].includes(labelName)) {
      return "Location Messages";
    }
    return "Other";
  }

  return "Other";
};

// Optimized label item - more user-friendly design
const LabelItem = React.memo(({
  labelKey,
  defaultLabel,
  customValue,
  commonValue,
  onChange,
  theme,
}: {
  labelKey: string;
  defaultLabel: string;
  customValue: string;
  commonValue?: string;
  onChange: (key: string, value: string) => void;
  theme: any;
}) => {
  const { module, labelName } = React.useMemo(() => getLabelParts(labelKey), [labelKey]);
  const hasCommonValue = module !== "common" && commonValue?.trim();
  const hasCustomValue = customValue.trim().length > 0;
  const displayValue = customValue.trim() || (hasCommonValue ? commonValue : defaultLabel);

  // Format label name to be more readable
  const readableLabelName = React.useMemo(() => {
    return labelName
      .replace(/([A-Z])/g, " $1")
      .replace(/^./, (str) => str.toUpperCase())
      .trim();
  }, [labelName]);

  const helperText = React.useMemo(() => {
    if (hasCustomValue) {
      return `✓ Customized`;
    }
    if (hasCommonValue) {
      return `Uses: "${commonValue}"`;
    }
    return `Default: "${defaultLabel}"`;
  }, [hasCustomValue, hasCommonValue, commonValue, defaultLabel]);

  return (
    <Grid item xs={12} md={6}>
      <Box
        sx={{
          position: "relative",
          p: 2,
          borderRadius: 2,
          border: hasCustomValue
            ? `2px solid ${theme.palette.primary.main}`
            : `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
          backgroundColor: hasCustomValue
            ? alpha(theme.palette.primary.main, 0.05)
            : alpha(theme.palette.background.paper, 0.5),
          transition: "all 0.2s ease",
          "&:hover": {
            borderColor: hasCustomValue
              ? theme.palette.primary.dark
              : theme.palette.primary.main,
            backgroundColor: hasCustomValue
              ? alpha(theme.palette.primary.main, 0.08)
              : alpha(theme.palette.primary.main, 0.03),
            transform: "translateY(-2px)",
            boxShadow: theme.shadows[2],
          },
        }}
      >
        {hasCustomValue && (
          <Chip
            icon={<CheckCircleOutlineIcon sx={{ fontSize: "0.875rem !important" }} />}
            label="Custom"
            size="small"
            sx={{
              position: "absolute",
              top: 12,
              right: 12,
              height: 24,
              fontSize: "0.7rem",
              fontWeight: 600,
              "& .MuiChip-icon": {
                fontSize: "0.875rem",
              },
            }}
          />
        )}
        <Box sx={{ mb: 1.5 }}>
          <Typography
            variant="subtitle2"
            sx={{
              fontWeight: 600,
              fontSize: "0.95rem",
              color: "text.primary",
              mb: 0.5,
              pr: hasCustomValue ? 8 : 0,
            }}
          >
            {readableLabelName}
          </Typography>
          <Typography
            variant="caption"
            sx={{
              display: "block",
              color: "text.secondary",
              fontSize: "0.7rem",
              fontFamily: "monospace",
              opacity: 0.6,
            }}
          >
            {labelKey}
          </Typography>
        </Box>
        <StyledTextField
          fullWidth
          size="medium"
          value={customValue}
          onChange={(e) => onChange(labelKey, e.target.value)}
          placeholder={defaultLabel}
          helperText={helperText}
          sx={{
            "& .MuiOutlinedInput-root": {
              backgroundColor: theme.palette.mode === "dark"
                ? alpha(theme.palette.background.paper, 0.8)
                : "white",
            },
            "& .MuiFormHelperText-root": {
              fontSize: "0.75rem",
              mt: 1,
              fontWeight: hasCustomValue ? 600 : 400,
              color: hasCustomValue ? theme.palette.success.main : "text.secondary",
            },
          }}
          inputProps={{
            maxLength: 100,
          }}
        />
      </Box>
    </Grid>
  );
}, (prev, next) => {
  // Custom comparison - only re-render if relevant props change
  return (
    prev.labelKey === next.labelKey &&
    prev.customValue === next.customValue &&
    prev.commonValue === next.commonValue &&
    prev.defaultLabel === next.defaultLabel &&
    prev.theme === next.theme
  );
});

LabelItem.displayName = "LabelItem";

const UIWordingSettingsPage = () => {
  useTitle("UI Wording Settings");

  // All hooks must be called in the same order every render
  const { data: companyLabels, isLoading, isFetching } = useGetUILabelsQuery();
  const [updateLabels, { isLoading: isUpdating }] = useUpdateUILabelsMutation();
  const [createLabels, { isLoading: isCreating }] = useCreateUILabelsMutation();

  // Form state: key -> value - must be before useMemo that depends on it
  const [formValues, setFormValues] = React.useState<Record<string, string>>(
    {},
  );
  const [hasChanges, setHasChanges] = React.useState(false);

  // Search state
  const [searchQuery, setSearchQuery] = React.useState("");
  const debouncedSearch = useDebounce(searchQuery, 300);

  // Track expanded modules - use Set for better performance
  const [expandedModules, setExpandedModules] = React.useState<Set<string>>(
    new Set(),
  );

  // Initialize expanded modules ref - must be before useMemo
  const hasInitializedExpanded = React.useRef(false);

  // All useMemo hooks - must be called in consistent order
  // Include all modules: Common, Project, Talent, Client, Manager, Location
  const allKeys = React.useMemo(() => {
    const keys: UILabelKey[] = [];
    // Common labels
    Object.keys(COMMON_LABELS).forEach((key) => {
      keys.push(`common.${key}` as UILabelKey);
    });
    // Project labels
    Object.keys(PROJECT_LABELS).forEach((key) => {
      keys.push(`project.${key}` as UILabelKey);
    });
    // Talent labels
    Object.keys(TALENT_LABELS).forEach((key) => {
      keys.push(`talent.${key}` as UILabelKey);
    });
    // Client labels
    Object.keys(CLIENT_LABELS).forEach((key) => {
      keys.push(`client.${key}` as UILabelKey);
    });
    // Manager labels
    Object.keys(MANAGER_LABELS).forEach((key) => {
      keys.push(`manager.${key}` as UILabelKey);
    });
    // Location labels
    Object.keys(LOCATION_LABELS).forEach((key) => {
      keys.push(`location.${key}` as UILabelKey);
    });
    return keys;
  }, []);

  // Pre-compute label parts to avoid repeated splits
  const labelPartsCache = React.useMemo(() => {
    const cache: Record<string, { module: string; labelName: string; }> = {};
    allKeys.forEach((key) => {
      cache[key] = getLabelParts(key);
    });
    return cache;
  }, [allKeys]);

  // Pre-compute default labels for search optimization
  const defaultLabelsCache = React.useMemo(() => {
    const cache: Record<string, string> = {};
    allKeys.forEach((key) => {
      cache[key] = getDefaultLabel(key);
    });
    return cache;
  }, [allKeys]);

  // Group labels by module and category
  const groupedLabels = React.useMemo(() => {
    const groups: Record<string, Record<string, UILabelKey[]>> = {};
    allKeys.forEach((key) => {
      const { module, labelName } = labelPartsCache[key];
      const category = categorizeLabel(module, labelName);

      if (!groups[module]) {
        groups[module] = {};
      }
      if (!groups[module][category]) {
        groups[module][category] = [];
      }
      groups[module][category].push(key);
    });
    return groups;
  }, [allKeys, labelPartsCache]);

  // Filter labels based on search
  const filteredGroupedLabels = React.useMemo(() => {
    if (!debouncedSearch.trim()) {
      return groupedLabels;
    }

    const searchLower = debouncedSearch.toLowerCase();
    const filtered: Record<string, Record<string, UILabelKey[]>> = {};

    Object.entries(groupedLabels).forEach(([module, categories]) => {
      const filteredCategories: Record<string, UILabelKey[]> = {};

      Object.entries(categories).forEach(([category, keys]) => {
        const filteredKeys = keys.filter((key) => {
          const { labelName } = labelPartsCache[key];
          const defaultLabel = defaultLabelsCache[key];
          return (
            key.toLowerCase().includes(searchLower) ||
            labelName.toLowerCase().includes(searchLower) ||
            defaultLabel.toLowerCase().includes(searchLower) ||
            category.toLowerCase().includes(searchLower)
          );
        });

        if (filteredKeys.length > 0) {
          filteredCategories[category] = filteredKeys;
        }
      });

      if (Object.keys(filteredCategories).length > 0) {
        filtered[module] = filteredCategories;
      }
    });

    return filtered;
  }, [groupedLabels, debouncedSearch, labelPartsCache, defaultLabelsCache]);

  // Pre-compute common values for inheritance display
  const commonValues = React.useMemo(() => {
    const common: Record<string, string> = {};
    Object.entries(formValues).forEach(([key, value]) => {
      const { module, labelName } = labelPartsCache[key] || getLabelParts(key);
      if (module === "common" && value.trim()) {
        common[labelName] = value.trim();
      }
    });
    return common;
  }, [formValues, labelPartsCache]);

  // Count custom labels
  const customLabelsCount = React.useMemo(() => {
    return Object.values(formValues).filter((v) => v.trim()).length;
  }, [formValues]);

  // Pre-compute module custom counts to avoid hooks in map
  const moduleCustomCounts = React.useMemo(() => {
    const counts: Record<string, number> = {};
    Object.entries(filteredGroupedLabels).forEach(([module, categories]) => {
      let total = 0;
      Object.values(categories).forEach((keys) => {
        total += keys.filter((key) => formValues[key]?.trim()).length;
      });
      counts[module] = total;
    });
    return counts;
  }, [filteredGroupedLabels, formValues]);

  // Track expanded categories
  const [expandedCategories, setExpandedCategories] = React.useState<Set<string>>(new Set());

  const toggleCategory = React.useCallback((categoryKey: string) => {
    setExpandedCategories((prev) => {
      const next = new Set(prev);
      if (next.has(categoryKey)) {
        next.delete(categoryKey);
      } else {
        next.add(categoryKey);
      }
      return next;
    });
  }, []);

  // All hooks must be called before any early returns
  const theme = useTheme();
  const totalLabels = React.useMemo(() => {
    let total = 0;
    Object.values(filteredGroupedLabels).forEach((categories) => {
      Object.values(categories).forEach((keys) => {
        total += keys.length;
      });
    });
    return total;
  }, [filteredGroupedLabels]);

  // Initialize expanded categories - expand first category of first module
  const hasInitializedCategories = React.useRef(false);
  React.useEffect(() => {
    if (!hasInitializedCategories.current && Object.keys(filteredGroupedLabels).length > 0) {
      const firstModule = Object.keys(filteredGroupedLabels)[0];
      if (firstModule && filteredGroupedLabels[firstModule]) {
        const firstCategory = Object.keys(filteredGroupedLabels[firstModule])[0];
        if (firstCategory) {
          setExpandedCategories(new Set([`${firstModule}.${firstCategory}`]));
          hasInitializedCategories.current = true;
        }
      }
    }
  }, [filteredGroupedLabels]);

  // All useEffect hooks - must be called after all useMemo
  // Initialize expanded modules after groupedLabels is ready (only once)
  React.useEffect(() => {
    if (!hasInitializedExpanded.current && Object.keys(groupedLabels).length > 0) {
      // Expand Common module by default (most important)
      const modules = Object.keys(groupedLabels).sort((a, b) => {
        if (a === "common") return -1;
        if (b === "common") return 1;
        return a.localeCompare(b);
      });
      if (modules.length > 0) {
        setExpandedModules(new Set([modules[0]])); // Expand Common first
        hasInitializedExpanded.current = true;
      }
    }
  }, [groupedLabels]);

  // Initialize form values from company labels or defaults (all modules)
  React.useEffect(() => {
    if (companyLabels || !isLoading) {
      const initialValues: Record<string, string> = {};
      allKeys.forEach((key) => {
        const customValue = companyLabels?.labels?.[key];
        initialValues[key] = customValue?.trim() || "";
      });
      setFormValues(initialValues);
      setHasChanges(false);
    }
  }, [companyLabels, isLoading, allKeys]);

  // All useCallback hooks - must be called after all useMemo and useEffect
  const handleChange = React.useCallback((key: string, value: string) => {
    setFormValues((prev) => {
      // Only update if value actually changed
      if (prev[key] === value) return prev;
      return { ...prev, [key]: value };
    });
    setHasChanges(true);
  }, []);

  const toggleModule = React.useCallback((module: string) => {
    setExpandedModules((prev) => {
      const next = new Set(prev);
      if (next.has(module)) {
        next.delete(module);
      } else {
        next.add(module);
      }
      return next;
    });
  }, []);

  const handleReset = async () => {
    const resetValues: Record<string, string> = {};
    allKeys.forEach((key) => {
      resetValues[key] = "";
    });
    setFormValues(resetValues);

    // Immediately save empty values to backend (reset all custom labels)
    try {
      if (companyLabels?.id) {
        // Update existing labels with empty object (removes all custom labels)
        try {
          await updateLabels({ labels: {} }).unwrap();
          toast.success("All UI labels reset successfully");
          setHasChanges(false);
        } catch (error: any) {
          // Handle 404: labels not found, try creating instead
          if (error?.status === 404 || error?.status === "FETCH_ERROR") {
            try {
              await createLabels({ labels: {} }).unwrap();
              toast.success("All UI labels reset successfully");
              setHasChanges(false);
            } catch (createError: any) {
              handleSaveError(createError);
            }
          } else {
            handleSaveError(error);
          }
        }
      } else {
        // No existing labels, nothing to reset
        toast.success("All UI labels reset successfully");
        setHasChanges(false);
      }
    } catch (error: any) {
      handleSaveError(error);
    }
  };

  const handleSave = async () => {
    try {
      // Filter out empty values (will fallback to default or inherit from common)
      // Empty strings are automatically filtered out per API documentation
      const labelsToSave: Record<string, string> = {};

      // Save all module labels (common and per-module overrides)
      // If a module label is empty, it will inherit from common via useUILabel hook
      Object.entries(formValues).forEach(([key, value]) => {
        if (value.trim()) {
          labelsToSave[key] = value.trim();
        }
      });

      if (companyLabels?.id) {
        // Update existing labels
        try {
          await updateLabels({ labels: labelsToSave }).unwrap();
          toast.success("UI labels updated successfully");
          setHasChanges(false);
        } catch (error: any) {
          // Handle 404: labels not found, try creating instead
          if (error?.status === 404 || error?.status === "FETCH_ERROR") {
            try {
              await createLabels({ labels: labelsToSave }).unwrap();
              toast.success("UI labels created successfully");
              setHasChanges(false);
            } catch (createError: any) {
              handleSaveError(createError);
            }
          } else {
            handleSaveError(error);
          }
        }
      } else {
        // Create new labels
        try {
          await createLabels({ labels: labelsToSave }).unwrap();
          toast.success("UI labels created successfully");
          setHasChanges(false);
        } catch (error: any) {
          // Handle 409: labels already exist, try updating instead
          if (error?.status === 409) {
            try {
              await updateLabels({ labels: labelsToSave }).unwrap();
              toast.success("UI labels updated successfully");
              setHasChanges(false);
            } catch (updateError: any) {
              handleSaveError(updateError);
            }
          } else {
            handleSaveError(error);
          }
        }
      }
    } catch (error: any) {
      handleSaveError(error);
    }
  };

  const handleSaveError = (error: any) => {
    const status = error?.status;
    const errorData = error?.data;

    // Handle specific status codes per API documentation
    if (status === 422) {
      // Validation error - show validation messages
      const validationErrors = errorData?.errors;
      if (validationErrors && typeof validationErrors === "object") {
        const errorMessages: string[] = [];
        Object.entries(validationErrors).forEach(([field, messages]) => {
          if (Array.isArray(messages)) {
            errorMessages.push(...messages);
          } else if (typeof messages === "string") {
            errorMessages.push(messages);
          }
        });
        if (errorMessages.length > 0) {
          toast.error(errorMessages.join(", "));
          return;
        }
      }
      toast.error(
        errorData?.message || "Validation error. Please check your input.",
      );
    } else if (status === 403) {
      toast.error(
        "You don't have permission to update UI labels. Please contact your administrator.",
      );
    } else if (status === 401) {
      toast.error("Your session has expired. Please log in again.");
    } else if (status === 404) {
      toast.error("UI labels not found. Please try refreshing the page.");
    } else {
      // Generic error
      const errorMessage =
        errorData?.message ||
        error?.message ||
        "Failed to save UI labels. Please try again.";
      toast.error(errorMessage);
    }
  };

  // Early return AFTER all hooks
  if (isLoading || isFetching) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "400px",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, width: "100%" }}>
      <CardBase
        title="UI Wording Settings"
        subtitle="Customize UI labels for your company. Project labels can inherit from Common labels if not customized."
        useGlassCard={true}
        sx={{ mb: 3 }}
      >
        {/* Info Alert */}
        <Alert
          severity="info"
          icon={<InfoOutlinedIcon />}
          sx={{
            mb: 3,
            borderRadius: 2,
            "& .MuiAlert-message": {
              width: "100%",
            },
          }}
        >
          <Typography variant="body2" sx={{ mb: 1 }}>
            <strong>How UI Wording Works:</strong>
          </Typography>
          <Typography variant="body2" component="div" sx={{ fontSize: "0.875rem", lineHeight: 1.7 }}>
            • <strong>Common Labels:</strong> Base labels used across the entire application (e.g., "Location", "Dashboard")
            <br />
            • <strong>Project Labels:</strong> Labels specific to Work/Project pages (e.g., "Work Information", "Create Job")
            <br />
            • <strong>Inheritance:</strong> If a Project label is empty, it automatically uses the Common label value
            <br />
            • <strong>Example:</strong> Set "project.location" = "Site" to override "common.location" = "Location" in Project pages only
            <br />
            • <strong>Empty fields:</strong> Will inherit from Common labels or use default values
            <br />
            • <strong>Changes apply:</strong> Immediately across all pages in your company account
          </Typography>
        </Alert>

        {/* Search Bar with Stats */}
        <CardBase
          useGlassCard={true}
          nested={true}
          sx={{
            mb: 3,
            p: 2,
            backgroundColor: alpha(theme.palette.primary.main, 0.05),
            border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
          }}
        >
          <Box
            sx={{
              display: "flex",
              gap: 2,
              alignItems: "center",
              flexWrap: "wrap",
            }}
          >
            <Box sx={{ flex: 1, minWidth: 250 }}>
              <StyledTextField
                fullWidth
                size="small"
                placeholder="Search labels by key, name, or default value..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" color="action" />
                    </InputAdornment>
                  ),
                  endAdornment: searchQuery && (
                    <InputAdornment position="end">
                      <IconButton
                        size="small"
                        onClick={() => setSearchQuery("")}
                        sx={{ p: 0.5 }}
                      >
                        <ClearIcon fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Box>
            <Stack direction="row" spacing={2} sx={{ alignItems: "center" }}>
              <Box sx={{ textAlign: "center" }}>
                <Typography
                  variant="h6"
                  sx={{ fontWeight: 600, color: "primary.main" }}
                >
                  {totalLabels}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {searchQuery ? "Found" : "Total"} Labels
                </Typography>
              </Box>
              {customLabelsCount > 0 && (
                <>
                  <Divider orientation="vertical" flexItem />
                  <Box sx={{ textAlign: "center" }}>
                    <Typography
                      variant="h6"
                      sx={{ fontWeight: 600, color: "success.main" }}
                    >
                      {customLabelsCount}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Custom
                    </Typography>
                  </Box>
                </>
              )}
            </Stack>
          </Box>
        </CardBase>

        {/* Collapsible Sections - Organized by Module and Category */}
        {/* Order: Common first, then Project */}
        <Stack spacing={3}>
          {Object.entries(filteredGroupedLabels)
            .sort(([a], [b]) => {
              // Common always first, then Project, then others alphabetically
              if (a === "common") return -1;
              if (b === "common") return 1;
              if (a === "project") return -1;
              if (b === "project") return 1;
              return a.localeCompare(b);
            })
            .map(([module, categories]) => {
              const isExpanded = expandedModules.has(module);
              const moduleCustomCount = moduleCustomCounts[module] || 0;
              const totalModuleLabels = Object.values(categories).reduce((sum, keys) => sum + keys.length, 0);

              return (
                <CardBase
                  key={module}
                  useGlassCard={true}
                  nested={true}
                  sx={{
                    overflow: "hidden",
                    transition: "all 0.2s ease",
                    "&:hover": {
                      transform: "translateY(-2px)",
                      boxShadow: theme.shadows[4],
                    },
                  }}
                >
                  <Box>
                    <Button
                      fullWidth
                      onClick={() => toggleModule(module)}
                      sx={{
                        justifyContent: "space-between",
                        px: 3,
                        py: 2.5,
                        textTransform: "none",
                        fontWeight: 600,
                        color: "text.primary",
                        backgroundColor: isExpanded ? alpha(theme.palette.primary.main, 0.08) : "transparent",
                        "&:hover": {
                          backgroundColor: alpha(theme.palette.primary.main, 0.12),
                        },
                      }}
                      startIcon={
                        isExpanded ? (
                          <ExpandLessIcon sx={{ color: "primary.main", fontSize: "1.5rem" }} />
                        ) : (
                          <ExpandMoreIcon sx={{ color: "primary.main", fontSize: "1.5rem" }} />
                        )
                      }
                    >
                      <Box sx={{ flex: 1, textAlign: "left" }}>
                        <Stack direction="row" spacing={2} sx={{ alignItems: "center", mb: 1 }}>
                          <Typography
                            variant="h5"
                            sx={{
                              fontWeight: 700,
                              fontSize: "1.3rem",
                              color: module === "common" ? "primary.main" :
                                module === "project" ? "secondary.main" :
                                  module === "talent" ? "info.main" :
                                    module === "client" ? "warning.main" :
                                      module === "manager" ? "success.main" :
                                        "error.main",
                            }}
                          >
                            {module === "common" ? "📋 Common Labels" :
                              module === "project" ? "💼 Project Labels" :
                                module === "talent" ? "👤 Talent Labels" :
                                  module === "client" ? "🏢 Client Labels" :
                                    module === "manager" ? "👔 Manager Labels" :
                                      "📍 Location Labels"}
                          </Typography>
                          <Chip
                            label={`${totalModuleLabels} labels`}
                            size="small"
                            variant="outlined"
                            color={module === "common" ? "primary" :
                              module === "project" ? "secondary" :
                                module === "talent" ? "info" :
                                  module === "client" ? "warning" :
                                    module === "manager" ? "success" :
                                      "error"}
                            sx={{ height: 28, fontSize: "0.8rem", fontWeight: 600 }}
                          />
                          {moduleCustomCount > 0 && (
                            <Chip
                              label={`${moduleCustomCount} customized`}
                              size="small"
                              color="success"
                              sx={{ height: 28, fontSize: "0.8rem", fontWeight: 600 }}
                            />
                          )}
                        </Stack>
                        {module === "common" ? (
                          <Typography variant="body2" color="text.secondary" sx={{ fontSize: "0.9rem", lineHeight: 1.7 }}>
                            Base labels used throughout the application. These labels appear in sidebar menus, common forms, and shared components.
                            Other module labels can inherit from these if not customized.
                          </Typography>
                        ) : module === "project" ? (
                          <Typography variant="body2" color="text.secondary" sx={{ fontSize: "0.9rem", lineHeight: 1.7 }}>
                            Labels specific to Work/Project pages including Create Job, Job Details, and Project Management.
                            Empty fields will automatically inherit from Common labels above.
                          </Typography>
                        ) : module === "talent" ? (
                          <Typography variant="body2" color="text.secondary" sx={{ fontSize: "0.9rem", lineHeight: 1.7 }}>
                            Labels for Independent Professionals/Talent pages including talent profiles, skills, certifications, and talent management.
                            Empty fields will automatically inherit from Common labels above.
                          </Typography>
                        ) : module === "client" ? (
                          <Typography variant="body2" color="text.secondary" sx={{ fontSize: "0.9rem", lineHeight: 1.7 }}>
                            Labels for Client pages including client profiles, client management, and client-related forms.
                            Empty fields will automatically inherit from Common labels above.
                          </Typography>
                        ) : module === "manager" ? (
                          <Typography variant="body2" color="text.secondary" sx={{ fontSize: "0.9rem", lineHeight: 1.7 }}>
                            Labels for Manager pages including manager profiles, manager management, and manager-related forms.
                            Empty fields will automatically inherit from Common labels above.
                          </Typography>
                        ) : (
                          <Typography variant="body2" color="text.secondary" sx={{ fontSize: "0.9rem", lineHeight: 1.7 }}>
                            Labels for Location pages including location management, location forms, and location-related features.
                            Empty fields will automatically inherit from Common labels above.
                          </Typography>
                        )}
                      </Box>
                    </Button>
                    <Collapse in={isExpanded}>
                      <Divider />
                      <Box sx={{ p: 3, backgroundColor: alpha(theme.palette.background.default, 0.3) }}>
                        <Stack spacing={3}>
                          {Object.entries(categories)
                            .sort(([a], [b]) => a.localeCompare(b))
                            .map(([category, keys]) => {
                              const categoryKey = `${module}.${category}`;
                              const isCategoryExpanded = expandedCategories.has(categoryKey);
                              const categoryCustomCount = keys.filter((key) => formValues[key]?.trim()).length;

                              return (
                                <CardBase
                                  key={categoryKey}
                                  useGlassCard={true}
                                  nested={true}
                                  sx={{
                                    border: `1px solid ${alpha(theme.palette.primary.main, 0.15)}`,
                                    backgroundColor: alpha(theme.palette.background.paper, 0.5),
                                  }}
                                >
                                  <Button
                                    fullWidth
                                    onClick={() => toggleCategory(categoryKey)}
                                    sx={{
                                      justifyContent: "space-between",
                                      px: 2,
                                      py: 1.5,
                                      textTransform: "none",
                                      fontWeight: 600,
                                      color: "text.primary",
                                      backgroundColor: isCategoryExpanded
                                        ? alpha(theme.palette.primary.main, 0.05)
                                        : "transparent",
                                      "&:hover": {
                                        backgroundColor: alpha(theme.palette.primary.main, 0.08),
                                      },
                                    }}
                                    startIcon={
                                      isCategoryExpanded ? (
                                        <ExpandLessIcon sx={{ fontSize: "1.2rem" }} />
                                      ) : (
                                        <ExpandMoreIcon sx={{ fontSize: "1.2rem" }} />
                                      )
                                    }
                                  >
                                    <Box sx={{ flex: 1, textAlign: "left" }}>
                                      <Stack direction="row" spacing={1.5} sx={{ alignItems: "center" }}>
                                        <Typography
                                          variant="subtitle1"
                                          sx={{
                                            fontWeight: 600,
                                            fontSize: "1rem",
                                          }}
                                        >
                                          {category}
                                        </Typography>
                                        <Chip
                                          label={keys.length}
                                          size="small"
                                          variant="outlined"
                                          sx={{ height: 22, fontSize: "0.7rem" }}
                                        />
                                        {categoryCustomCount > 0 && (
                                          <Chip
                                            label={`${categoryCustomCount} custom`}
                                            size="small"
                                            color="success"
                                            sx={{ height: 22, fontSize: "0.7rem" }}
                                          />
                                        )}
                                      </Stack>
                                    </Box>
                                  </Button>
                                  <Collapse in={isCategoryExpanded}>
                                    <Divider />
                                    <Box sx={{ p: 2.5 }}>
                                      <Grid container spacing={2}>
                                        {keys.map((key) => {
                                          const defaultLabel = defaultLabelsCache[key];
                                          const customValue = formValues[key] || "";
                                          const { labelName } = labelPartsCache[key];
                                          const commonValue = commonValues[labelName];

                                          return (
                                            <LabelItem
                                              key={key}
                                              labelKey={key}
                                              defaultLabel={defaultLabel}
                                              customValue={customValue}
                                              commonValue={commonValue}
                                              onChange={handleChange}
                                              theme={theme}
                                            />
                                          );
                                        })}
                                      </Grid>
                                    </Box>
                                  </Collapse>
                                </CardBase>
                              );
                            })}
                        </Stack>
                      </Box>
                    </Collapse>
                  </Box>
                </CardBase>
              );
            })}
        </Stack>

        {/* Sticky Action Bar */}
        <CardBase
          useGlassCard={true}
          nested={true}
          sx={{
            position: "sticky",
            bottom: 0,
            mt: 4,
            p: 2,
            zIndex: 10,
          }}
        >
          <Stack
            direction="row"
            spacing={2}
            sx={{ justifyContent: "flex-end", alignItems: "center" }}
          >
            {hasChanges && (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mr: "auto" }}
              >
                You have unsaved changes
              </Typography>
            )}
            <LoadingButton
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={handleReset}
              loading={isUpdating || isCreating}
              sx={{ minWidth: 150 }}
            >
              Reset
            </LoadingButton>
            <LoadingButton
              variant="contained"
              startIcon={<SaveIcon />}
              onClick={handleSave}
              loading={isUpdating || isCreating}
              disabled={!hasChanges}
              sx={{ minWidth: 150 }}
            >
              Save Changes
            </LoadingButton>
          </Stack>
        </CardBase>
      </CardBase>
    </Box>
  );
};

export default UIWordingSettingsPage;

//here is where you can customize the UI wording settings for each module with inheritance
